import moment from 'moment'
import { API, SportsKind } from '@/models/core'
import { User } from '@/models/user'
import { Enum } from '@/helpers/enum'

export const ContestRewardsKinds = new Enum({
  NoRewards: 'NoRewards',
  MedalByRank: 'MedalByRank',
  ChipsByRank: 'ChipsByRank',
  ChipsByPoint: 'ChipsByPoint'
})

export const ContestTemplateKinds = new Enum({
  Season: 'Season',
  Monthly: 'Monthly'
})

export class FantasyRule {
  static createFromJson (raw) {
    let obj = new FantasyRule()
    obj = Object.assign(obj, raw)
    // if (!obj.name) {
    //   obj.name = 'Default Rule'
    // }
    return obj
  }

  static fetch (contestKey) {
    let params = {}

    return API.instance().get(`/fantasy-game/${API.faKey}/contest/${contestKey}/rule/`, params).then((resp) => {
      let raw = resp.data.data
      const obj = FantasyRule.createFromJson(raw)
      return obj
    })
  }
}

export class FantasyReward {
  static createFromJson (raw) {
    let obj = new FantasyReward()
    obj = Object.assign(obj, raw)
    obj.kind = ContestRewardsKinds.parse(obj.kind)
    return obj
  }

  static fetch (contestKey) {
    let params = {}

    return API.instance().get(`/fantasy-game/${API.faKey}/contest/${contestKey}/rewards/`, params).then((resp) => {
      let raw = resp.data.data
      const obj = FantasyReward.createFromJson(raw)
      return obj
    })
  }
}

export class FantasyPointsystem {
  static createFromJson (raw) {
    let obj = new FantasyPointsystem()
    obj = Object.assign(obj, raw)
    return obj
  }

  static fetch (contestKey) {
    let params = {}

    return API.instance().get(`/fantasy-game/${API.faKey}/contest/${contestKey}/pointsystem/`, params).then((resp) => {
      let raw = resp.data.data
      const obj = FantasyPointsystem.createFromJson(raw)
      return obj
    })
  }
}

export class MatchMeta {
  get link () {
    // Link is hardcoded here because eventually this will
    // be on different website
    if (this.sport.isCricket) {
      return `/cricket/match/${this.key}/`
    } else {
      return `/match/${this.key}/`
    }
  }
  static createFromJson (raw) {
    let obj = new MatchMeta()
    obj = Object.assign(obj, raw)
    obj.startAt = moment.unix(raw.start_at)
    obj.sport = SportsKind.parse(raw.sport)

    const parts = raw.title.split('vs')
    if (raw.teama_name.length > 4 && parts.length === 2) {
      obj.teamAName = parts[0].replace(' ', '')
      obj.teamBName = parts[1].replace(' ', '')
    } else {
      obj.teamAName = raw.teama_name
      obj.teamBName = raw.teamb_name
    }
    return obj
  }
}

export class PlayerMeta {
  static createFromJson (raw) {
    let obj = new PlayerMeta()
    obj = Object.assign(obj, raw)
    return obj
  }
}

export class TeamMeta {
  static createFromJson (raw) {
    let obj = new TeamMeta()
    obj = Object.assign(obj, raw)
    return obj
  }
}

export class FantasyContest {
  userMatchTeam (userKey, matchKey) {
    let params = {
      userKey, matchKey
    }

    return API.instance().get(`/fantasy/contest/${this.key}/user-team/`, { params }).then((resp) => {
      const userTeam = FantasyUserTeam.createFromJson(resp.data.data.userteam)
      const info = resp.data.data.info
      const calc = resp.data.data.calc
      const forUser = resp.data.data.for_user

      return { userTeam, info, calc, forUser }
    })
  }

  static fetch (key) {
    let params = {}

    return API.instance().get(`/fantasy/contest/${key}/`, params).then((resp) => {
      let contest = resp.data.data.contest
      contest = FantasyContest.createFromJson(contest)
      let template = null
      const membersCount = resp.data.data.members_count
      const canEdit = resp.data.data.can_edit

      if (resp.data.data.contest_template) {
        template = FantasyContestCreateTemplate.createFromJson(resp.data.data.contest_template)
      }

      return { contest, template, membersCount, canEdit }
    })
  }

  static createFromJson (raw) {
    let contest = new FantasyContest()
    contest = Object.assign(contest, raw)
    contest.rule = FantasyRule.createFromJson(contest.rule)
    contest.rewards = FantasyReward.createFromJson(contest.rewards)
    contest.sport = SportsKind.parse(contest.sport)
    if (contest.created_by) {
      contest.created_by = User.createFromJson(contest.created_by)
    }
    return contest
  }
}

export class FantasyContestCreateTemplate {
  static createFromJson (raw) {
    let template = new FantasyContestCreateTemplate()
    template = Object.assign(template, raw)
    template.kind = ContestTemplateKinds.parse(template.kind)
    template.created_by = User.createFromJson(template.created_by)

    if (template.contest) {
      template.contest = FantasyContest.createFromJson(template.contest)
    }
    return template
  }
}

export class FantasyContestMatch {
  participate (userTeamKey, buyInChips) {
    const data = {
      userTeamKey: userTeamKey,
      buyInChips: buyInChips
    }

    return API.instance().post(`/fantasy/contest/match/${this.key}/participate/`, data).then((resp) => {
      let participants = resp.data.data.participants
      participants = participants.map((raw) => FantasyMatchParticipation.createFromJson(raw))
      return { participants }
    })
  }

  fetchParticipants () {
    return API.instance().get(`/fantasy/contest/match/${this.key}/participate/`).then((resp) => {
      let participants = resp.data.data.participants
      participants = participants.map((raw) => FantasyMatchParticipation.createFromJson(raw))
      return { participants }
    })
  }

  resign (userTeamKey) {
    return FantasyContestMatch.resignForKey(this.key, userTeamKey)
  }

  static resignForKey (key, userTeamKey) {
    const data = {
      userTeamKey: userTeamKey,
      action: 'DELETE'
    }

    return API.instance().post(`/fantasy/contest/match/${key}/participate/`, data).then((resp) => {
      return true
    })
  }

  static discover (matchKey) {
    let params = {}

    return API.instance().get(`/fantasy/contest/match/${matchKey}/discover/`, params).then((resp) => {
      let contestMatches = resp.data.data.contest_matches
      contestMatches = contestMatches.map((raw) => FantasyContestMatch.createFromJson(raw))
      return contestMatches
    })
  }

  static createFromJson (raw) {
    let contestMatch = new FantasyContestMatch()
    contestMatch = Object.assign(contestMatch, raw)
    contestMatch.match_meta = MatchMeta.createFromJson(contestMatch.match_meta)
    if (contestMatch.contest.rule) {
      contestMatch.contest = FantasyContest.createFromJson(contestMatch.contest)
    }
    return contestMatch
  }
}

export class FantasyMatchParticipation {
  static overview (matchKey) {
    let params = {}

    return API.instance().get(`/fantasy/contest/match/${matchKey}/overview/`, params).then((resp) => {
      let participations = resp.data.data.participations
      participations = participations.map((raw) => FantasyMatchParticipation.createFromJson(raw))

      let userTeams = resp.data.data.userteams
      userTeams = userTeams.map((raw) => FantasyUserTeam.createFromJson(raw))

      return { participations, userTeams }
    })
  }

  static createFromJson (raw) {
    let participation = new FantasyMatchParticipation()
    participation = Object.assign(participation, raw)
    if (participation.contest.rule) {
      participation.contest = FantasyContest.createFromJson(participation.contest)
    }
    if (participation.contest_match.contest) {
      participation.contestMatch = FantasyContestMatch.createFromJson(participation.contest_match)
      if (participation.contest) {
        participation.contestMatch.contest = participation.contest
      }
    }
    participation.user = User.createFromJson(participation.user)
    participation.teamUpdated = moment(raw.user_team_updated)
    return participation
  }
}

export class FantasyUserTeam {
  boostNumber (playerKey) {
    let number = this.team.boost_players.indexOf(playerKey)
    return number + 1
  }

  deleteTeam () {
    const postData = { key: this.key, action: 'DELETE' }
    return API.instance().post(`/fantasy/team/`, postData).then((resp) => {
      return true
    })
  }

  static teamsByRule (matchKey, ruleKey) {
    const params = {
      rule: ruleKey
    }

    return API.instance().get(`/fantasy/contest/match/${matchKey}/user-teams/`, { params }).then((resp) => {
      let userTeams = resp.data.data.userteams
      userTeams = userTeams.map((raw) => FantasyUserTeam.createFromJson(raw))
      return userTeams
    })
  }

  static createFromJson (raw) {
    let contest = new FantasyUserTeam()
    contest = Object.assign(contest, raw)
    if (contest.user) {
      contest.user = User.createFromJson(contest.user)
    }
    return contest
  }
}

export class FantasyContestLeaderboard {
  static fetch (cmKey) {
    let params = {}

    return API.instance().get(`/fantasy/contest/${cmKey}/leaderboard/`, params).then((resp) => {
      let leaderboard = resp.data.data.leaderboard
      let cursor = resp.data.data.cursor
      leaderboard = leaderboard.map((raw) => FantasyContestLeaderboard.createFromJson(raw))
      return { leaderboard, cursor }
    })
  }

  static createFromJson (raw) {
    let obj = new FantasyContestLeaderboard()
    obj = Object.assign(obj, raw)
    obj.user = User.createFromJson(obj.user)
    return obj
  }
}

export class FantasyMatchLeaderboard {
  static fetch (cmKey) {
    let params = {}

    return API.instance().get(`/fantasy/contest/match/${cmKey}/leaderboard/`, params).then((resp) => {
      let leaderboard = resp.data.data.leaderboard
      let info = resp.data.data.info
      info.updated = moment.unix(info.match_updated_ts)
      leaderboard = leaderboard.map((raw) => FantasyMatchLeaderboard.createFromJson(raw))
      return { leaderboard, info }
    })
  }

  static createFromJson (raw) {
    let obj = new FantasyMatchLeaderboard()
    obj = Object.assign(obj, raw)
    obj.user = User.createFromJson(obj.user)
    return obj
  }
}

export class FantasyPlayerSelectionState {
  constructor (player) {
    this.key = player.key
    this.player = player
    this.canSelect = true
    this.reason = null
    this.boostName = null
    this.selected = -1
    this.usedCredit = null
    this.credit = null
  }
}

export class FantasyTeamSelector {
  constructor (contestMatch, match, playerCredits, userTeam) {
    this.contestMatch = contestMatch
    this.match = match
    this.playerCredits = playerCredits
    this.userTeam = userTeam || null

    this.rule = contestMatch.contest.rule

    this.playerKeys = []
    this.playersState = {}

    this.creditUsed = 0
    this.rolesCount = {}
    this.teamsCount = {}
    this.validTeam = ''

    this._ts = new Date().getTime()

    Object.keys(match.players).map((key) => {
      this.initPlayer(key)
    })

    if (userTeam) {
      this.updateUserTeam(userTeam)
    }

    this.updatePlayerState()
  }

  updateUserTeam (userTeam, clone) {
    if (!clone) {
      this.userTeam = userTeam
    }

    userTeam.team.players.map((key, i) => {
      this.removePlayer(key)
      this.addPlayer(key, userTeam.credits_used[i])
    })

    userTeam.team.boost_players.map((key, i) => {
      this.addBoostPlayer(i, key)
    })
  }

  initPlayer (key) {
    const selectionState = new FantasyPlayerSelectionState(this.match.players[key])

    // Player Fantansy Credit may be missing in Credit Variation API
    if (this.playerCredits && this.playerCredits.players[key].fantasyCredit) {
      selectionState.credit = this.playerCredits.players[key].fantasyCredit.credit_value
    } else if (this.playerCredits) {
      console.log(`Player Fantasy Missing ${key}`)
      console.log(`Player Fantasy Missing ${key} > ${this.playerCredits.players[key].name}`)
    }

    let playersTeamIndexKey = null

    if (this.match.sport.isFootball) {
      if (this.match.info.home.players.indexOf(key) > -1) {
        playersTeamIndexKey = 'a'
      } else {
        playersTeamIndexKey = 'b'
      }
    } else {
      if (this.match.teams.a.match.players.indexOf(key) > -1) {
        playersTeamIndexKey = 'a'
      } else {
        playersTeamIndexKey = 'b'
      }
    }

    selectionState.team_key = playersTeamIndexKey
    this.playersState[key] = selectionState
  }

  updatePlayerCredits (credits) {
    this.playerCredits = credits
    Object.keys(this.playersState).map((key) => {
      // Player Fantansy Credit may be missing in Credit Variation API
      if (this.playerCredits.players[key].fantasyCredit) {
        const playerState = this.playersState[key]
        if (this.playerCredits.players[key].fantasyCredit.credit_value) {
          // For Cricket
          playerState.credit = this.playerCredits.players[key].fantasyCredit.credit_value
        } else {
          // For Football
          playerState.credit = this.playerCredits.players[key].fantasyCredit.credits
        }
      } else {
        console.log(`Player Fantasy Missing ${key}`)
        console.log(`Player Fantasy Missing ${key} > ${this.playerCredits.players[key].name}`)
      }
    })
  }

  selectable (playerKey) {
    const playerState = this.playersState[playerKey]
    if (!playerState) {
      return false
    }

    return playerState.canSelect
  }

  isSelected (playerKey) {
    const playerState = this.playersState[playerKey]
    if (!playerState) {
      return false
    }

    return playerState.selected > -1
  }

  selectableReason (playerKey) {
    const playerState = this.playersState[playerKey]
    if (!playerState) {
      return `${playerKey} players are not allowed for this match`
    }

    return playerState.reason
  }

  canSelectPlayer (playerKey) {
    const playerState = this.playersState[playerKey]
    if (playerState.selected > -1) {
      return 'Already selected'
    }

    const player = playerState.player
    const role = player.fantasyRole.value

    if (this.rule.max_players && this.playerKeys.length === this.rule.max_players) {
      return `${this.rule.max_players} players already selected`
    }

    if (!this.teamsCount[playerState.team_key]) {
      this.teamsCount[playerState.team_key] = 0
    }

    if (!this.rolesCount[role]) {
      this.rolesCount[role] = 0
    }

    if (this.rule.max_players_per_team && this.teamsCount[playerState.team_key] === this.rule.max_players_per_team) {
      return `Already selected ${this.rule.max_players_per_team} players from the same team`
    }

    if (this.rule.max_players_by_role) {
      if (this.rule.max_players_by_role[role] && this.rule.max_players_by_role[role] === this.rolesCount[role]) {
        return `${this.rolesCount[role]} ${role} selected`
      }
    }

    if (this.playerCredits) {
      const creditRequired = playerState.credit
      const totalRequired = this.creditUsed + creditRequired
      if (totalRequired > this.rule.credits) {
        const req = totalRequired - this.rule.credits
        return `Not enough credits. You need ${req} more.`
      }
    }

    return true
  }

  isRulePassedPlayersCount () {
    const count = this.playerKeys.length
    return (count >= this.rule.min_players) && (count <= this.rule.min_players)
  }

  isRulePassedPlayersRoleCount (role) {
    const [allowedMin, allowedMax] = this.rolePlayerRange(role)
    const count = this.rolesCount[role] || 0
    return (count >= allowedMin) && (count <= allowedMax)
  }

  rolePlayerRange (role) {
    const allowedMin = this.rule.min_players_by_role[role]
    const allowedMax = this.rule.max_players_by_role[role] || this.rule.max_players
    return [allowedMin, allowedMax]
  }

  validateTeam () {
    if (this.playerKeys.length < this.rule.min_players) {
      return `You must select ${this.rule.min_players} players`
    }

    if (this.rule.min_players_by_role) {
      const roleKeys = Object.keys(this.rule.min_players_by_role)
      for (let i = 0; i < roleKeys.length; i++) {
        const role = roleKeys[i]
        const allowed = this.rule.min_players_by_role[role]
        const count = this.rolesCount[role] || 0
        if (count < allowed) {
          return `Must select atleast ${allowed} ${role}`
        }
      }
    }

    const boostPlayers = this.selectedBoostPlayers()
    for (let i = 0; i < this.rule.boost_names.length; i++) {
      const name = this.rule.boost_names[i]
      const count = boostPlayers[i]
      if (!count) {
        return `Must select a "${name}" from your players`
      }
    }

    return true
  }

  updatePlayerState () {
    this.creditUsed = 0
    this.rolesCount = {}
    this.teamsCount = {}

    this.playerKeys.map((key) => {
      const playerState = this.playersState[key]
      const player = playerState.player
      const playerRole = player.fantasyRole.value
      this.creditUsed += playerState.usedCredit

      if (!this.rolesCount[playerRole]) {
        this.rolesCount[playerRole] = 0
      }

      if (!this.teamsCount[playerState.team_key]) {
        this.teamsCount[playerState.team_key] = 0
      }

      this.rolesCount[playerRole]++
      this.teamsCount[playerState.team_key]++
    })

    Object.keys(this.playersState).map((key) => {
      const reason = this.canSelectPlayer(key)
      const playerState = this.playersState[key]

      if (reason === true) {
        playerState.canSelect = true
        playerState.reason = true
      } else {
        playerState.canSelect = false
        playerState.reason = reason
      }
    })

    this.validTeam = this.validateTeam()
  }

  addPlayer (playerKey, credit) {
    if (credit === null) {
      credit = this.playersState[playerKey].credit
    }
    this.playersState[playerKey].selected = this.playerKeys.length
    this.playersState[playerKey].usedCredit = credit
    this.playerKeys.push(playerKey)
    this.updatePlayerState()
  }

  removePlayer (playerKey) {
    const index = this.playersState[playerKey].selected
    if (index > -1) {
      this.playerKeys.splice(index, 1)

      for (let i = index; i < this.playerKeys.length; i++) {
        const key = this.playerKeys[i]
        this.playersState[key].selected += -1
      }

      this.playersState[playerKey].selected = -1
      this.removePlayerBoost(playerKey)
      this.updatePlayerState()
    }
  }

  addBoostPlayer (index, playerKey) {
    const name = this.rule.boost_names[index]
    this.removeBoostName(name)
    this.playersState[playerKey].boostName = name
    this.updatePlayerState()
  }

  removeBoostName (name) {
    let found = null
    Object.keys(this.playersState).map((key) => {
      if (this.playersState[key].boostName === name) {
        found = key
        this.playersState[key].boostName = null
      }
    })
    return found
  }

  removePlayerBoost (playerKey) {
    const name = this.playersState[playerKey].boostName
    if (name) {
      this.removeBoostName(name)
      this.updatePlayerState()
    }
  }

  rangeLabel (minValue, maxValue, joinString) {
    if (minValue === maxValue) {
      return minValue
    }

    joinString = joinString || ' - '
    return `${minValue}${joinString}${maxValue}`
  }

  selectedBoostPlayers () {
    let boostKeys = Array(this.rule.boost_names.length).fill(null)
    Object.keys(this.playersState).map((key) => {
      if (this.playersState[key].boostName) {
        const name = this.playersState[key].boostName
        const index = this.rule.boost_names.indexOf(name)
        boostKeys[index] = key
      }
    })

    return boostKeys
  }

  isEdit () {
    return this.userTeam && this.userTeam.key
  }

  save () {
    let boostKeys = this.selectedBoostPlayers()
    let key = null
    if (this.userTeam && this.userTeam.key) {
      key = this.userTeam.key
    }

    const matchKey = (this.match.sport.isFootball) ? this.match.info.key : this.match.key
    const postData = {
      rule: this.rule.key,
      key: key,
      matchKey: matchKey,
      players: this.playerKeys,
      boostPlayers: boostKeys
    }

    let sportRaw = this.contestMatch.contest.sport
    if (sportRaw.value) {
      sportRaw = sportRaw.value
    }
    const params = {
      sport: sportRaw
    }

    return API.instance().post(`/fantasy/team/`, postData, { params }).then((resp) => {
      let teamRaw = resp.data.data.user_team
      const userTeam = FantasyUserTeam.createFromJson(teamRaw)
      this.updateUserTeam(userTeam)
      return userTeam
    })
  }
}
